import {FC} from 'react';
import {Box, Button, CircularProgress, MenuItem, Select, SelectChangeEvent, Typography} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useIntl} from 'react-intl';
import {useBusinessCurrency} from 'src/app/hooks';
import {rowCurrency} from 'src/utils/reportMoney';
import {useScreenTier} from 'src/hooks/useScreenTier';
import theme from 'src/theme';
import {ConsumptionTotals} from 'src/types';
import {tableSx} from './BreakdownTable.sx';

/**
 * The design's three tones, by where a row sits in the list: the largest contributor carries the
 * primary blue, the one under it a lighter shade, everything after that lighter again.
 */
const ROW_TONES: string[] = [
    theme.palette.primary.main,
    theme.palette.primaryColor.A300 as string,
    theme.palette.primaryColor[100] as string,
];

export interface BreakdownRow extends ConsumptionTotals {
    id: string;
    name: string;
    subtitle?: string;
}

export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

interface BreakdownTableProps {
    rows: BreakdownRow[];
    loading: boolean;
    maxConsumption: number;
    page: number;
    size: number;
    totalElements: number;
    onPageChange: (page: number) => void;
    onSizeChange: (size: number) => void;
}

export const BreakdownTable: FC<BreakdownTableProps> = ({
    rows,
    loading,
    maxConsumption,
    page,
    size,
    totalElements,
    onPageChange,
    onSizeChange,
}) => {
    const intl = useIntl();
    // Each row names its own currency; this is only for a row that arrived without one.
    const fallbackCurrency = useBusinessCurrency();
    // Below md the grid of columns becomes one row per record; from md up the table is untouched.
    const {isCompact} = useScreenTier();
    const totalPages = Math.max(1, Math.ceil(totalElements / size));

    const handleSizeChange = (e: SelectChangeEvent<number>) => {
        onSizeChange(Number(e.target.value));
    };

    const startItem = totalElements === 0 ? 0 : page * size + 1;
    const endItem = Math.min((page + 1) * size, totalElements);

    const renderBody = () => {
        if (loading && rows.length === 0) {
            return (
                <Box sx={tableSx.bodyLoading}>
                    <CircularProgress size={28} />
                </Box>
            );
        }
        if (!loading && rows.length === 0) {
            return (
                <Box sx={tableSx.bodyEmpty}>
                    {intl.formatMessage({id: 'ecNoData'})}
                </Box>
            );
        }
        return rows.map((row, index) => {
            const kWh = row.consumption / 1000;
            const cost = (row.cost ?? 0) / 10000;
            const currency = rowCurrency(row, fallbackCurrency);
            const avgPrice = row.averagePrice ? row.averagePrice / 10000 : 0;
            const percent = maxConsumption > 0 ? (row.consumption / maxConsumption) * 100 : 0;

            if (isCompact) {
                // The same figures the table shows, in the order a phone can read them: the name and
                // its kWh on one line, the share of the largest value under both.
                return (
                    <Box key={`${row.id}-${row.currency ?? ''}`} sx={tableSx.phoneRow}>
                        <Box sx={tableSx.phoneRowTop}>
                            <Box sx={{minWidth: 0}}>
                                <Typography sx={tableSx.phoneName}>{row.name}</Typography>
                                {row.subtitle && (
                                    <Typography sx={tableSx.phoneSubtitle}>{row.subtitle}</Typography>
                                )}
                            </Box>
                            <Typography sx={tableSx.phoneValue}>
                                {kWh.toFixed(1)} kWh
                            </Typography>
                        </Box>
                        <Box sx={tableSx.phoneBarTrack}>
                            <Box
                                sx={{
                                    ...tableSx.phoneBarFill,
                                    width: `${percent}%`,
                                    bgcolor: ROW_TONES[Math.min(index, ROW_TONES.length - 1)],
                                }}
                            />
                        </Box>
                    </Box>
                );
            }

            return (
                <Box key={`${row.id}-${row.currency ?? ''}`} sx={tableSx.row}>
                    <Box sx={{minWidth: 0}}>
                        <Typography sx={tableSx.name}>{row.name}</Typography>
                        {row.subtitle && (
                            <Typography sx={tableSx.nameSubtitle}>{row.subtitle}</Typography>
                        )}
                    </Box>
                    <Box sx={tableSx.barCell}>
                        <Box sx={tableSx.barTrack}>
                            <Box sx={{...tableSx.barFill, width: `${percent}%`}} />
                        </Box>
                        <Typography sx={tableSx.barValue}>
                            {kWh.toFixed(1)} kWh
                        </Typography>
                    </Box>
                    <Typography sx={tableSx.metricRight}>
                        {cost.toFixed(2)} {currency}
                    </Typography>
                    <Typography sx={tableSx.metricRight}>
                        {row.transactionCount}
                    </Typography>
                    <Typography sx={tableSx.metricHideMobile}>
                        {avgPrice.toFixed(3)}
                    </Typography>
                </Box>
            );
        });
    };

    return (
        <Box sx={tableSx.root}>
            <Box sx={tableSx.headerRow}>
                <Typography sx={tableSx.headerCell}>
                    {intl.formatMessage({id: 'ecColumnName'})}
                </Typography>
                <Typography sx={tableSx.headerCell}>
                    {intl.formatMessage({id: 'ecColumnConsumption'})}
                </Typography>
                <Typography sx={tableSx.headerCellRight}>
                    {intl.formatMessage({id: 'ecColumnCost'})}
                </Typography>
                <Typography sx={tableSx.headerCellRight}>
                    {intl.formatMessage({id: 'ecColumnSessions'})}
                </Typography>
                <Typography sx={{...tableSx.headerCellRight, '@media (max-width: 900px)': {display: 'none'}}}>
                    {intl.formatMessage({id: 'ecColumnAvgPrice'})}
                </Typography>
            </Box>

            <Box sx={tableSx.body}>{renderBody()}</Box>

            <Box sx={tableSx.footer}>
                <Box sx={tableSx.footerLeft}>
                    <Typography sx={tableSx.footerLabelHideMobile}>
                        {intl.formatMessage({id: 'ecRowsPerPage'})}
                    </Typography>
                    <Select
                        value={size}
                        onChange={handleSizeChange}
                        sx={tableSx.sizeSelect}
                        size="small"
                    >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={tableSx.footerRight}>
                    <Typography sx={tableSx.footerLabelHideMobile}>
                        {startItem}–{endItem} / {totalElements}
                    </Typography>
                    <Button
                        sx={tableSx.pageBtn}
                        onClick={() => onPageChange(page - 1)}
                        disabled={page === 0 || loading}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </Button>
                    <Typography sx={tableSx.pageInfo}>
                        {page + 1} / {totalPages}
                    </Typography>
                    <Button
                        sx={tableSx.pageBtn}
                        onClick={() => onPageChange(page + 1)}
                        disabled={page >= totalPages - 1 || loading}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
